import React, { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useUrgentVisitStore } from "../../../../hooks/redux-hooks/urgent-visit-store/urgent-visit.store";
import { useVerifyPromoCode } from "../../../../services/promo-code-service/promo-code.service";
import PromoCode, { PromoCodeUsed } from "../../../../components/PromoCode/PromoCode";

type AppliedPromo = {
  _id: string;
  promoCode: string;
  description: string;
};

interface ReviewPromoCodeProps {
  readOnlyPromoCodes?: string[];
}

export const ReviewPromoCode = ({ readOnlyPromoCodes }: ReviewPromoCodeProps) => {
  const { selectedPets, selectedSlot, setPromoCodes, selectedLocationId } = useUrgentVisitStore();
  const { getVerifyPromoCode, loading, promoCodeResponse } = useVerifyPromoCode();

  const [inputValue, setInputValue] = useState("");
  const [appliedCodes, setAppliedCodes] = useState<AppliedPromo[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const alphanumericRegex = /^[a-zA-Z0-9]*$/;
    if (alphanumericRegex.test(e.target.value) && e.target.value.length <= 20) {
      setInputValue(e.target.value.toUpperCase());
      if (errorMsg) setErrorMsg("");
    }
  };

  const handleApply = () => {
    if (!inputValue.trim()) {
      setErrorMsg("Please enter a promo code");
      return;
    }

    const isAlreadyApplied = appliedCodes.some(
      (code) => code.promoCode === inputValue.trim(),
    );
    if (isAlreadyApplied) {
      setErrorMsg("Promo code already applied");
      return;
    }

    getVerifyPromoCode({
      bookingTime: Number(selectedSlot?.time),
      previousAppliedPromos: appliedCodes.map((c) => c.promoCode),
      locationId: selectedLocationId || undefined,
      pets: selectedPets.map((pet) => ({
        petId: pet.id,
        promoCode: inputValue.trim(),
      })),
    });
  };

  useEffect(() => {
    if (promoCodeResponse && !loading) {
      if (promoCodeResponse.success && promoCodeResponse.basePromoCode?.length) {
        setAppliedCodes((prev) => {
          const isAlreadyApplied = prev.some(
            (code) => code._id === promoCodeResponse.basePromoCode[0]?._id,
          );
          if (!isAlreadyApplied) {
            setSuccessMsg(promoCodeResponse.messages?.[0] || null);
            return [
              ...prev,
              {
                _id: promoCodeResponse.basePromoCode[0]._id,
                promoCode: promoCodeResponse.basePromoCode[0].promoCode,
                description: promoCodeResponse.basePromoCode[0].description,
              },
            ];
          }
          return prev;
        });
        setInputValue("");
        setErrorMsg("");
      }
      if (!promoCodeResponse.success) {
        setErrorMsg(promoCodeResponse.messages?.[0] || "Invalid promo code");
      }
    }
  }, [promoCodeResponse, loading]);

  useEffect(() => {
    setPromoCodes(appliedCodes.map((c) => c.promoCode));
  }, [appliedCodes]);

  const handleRemoveCode = (id: string) => {
    setAppliedCodes((prev) => prev.filter((code) => code._id !== id));
  };

  if (readOnlyPromoCodes) {
    return (
      <div className="flex flex-col gap-2 opacity-60">
        <p className="font-semibold text-sm text-gray-1 leading-4">
          {"Promo Code"}
        </p>
        <div className="bg-white rounded-lg border border-taupe-50 p-3 flex flex-col gap-3">
          {readOnlyPromoCodes.map((code) => (
            <PromoCodeUsed key={code} promoCodeUsed={code} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="font-semibold text-sm text-light-black leading-4">
        {"Promo Code"}
      </p>
      <div className="bg-white rounded-lg border border-taupe-50 p-3 flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => {
              if (errorMsg) setErrorMsg("");
            }}
            placeholder={"Enter Promo Code"}
            className={`flex-1 text-sm text-light-black placeholder-gray-4
              outline-none bg-transparent border rounded-lg px-2 py-3
              ${errorMsg ? "border-red-500" : "border-taupe-50"}`}
          />
          <button
            type="button"
            onClick={handleApply}
            disabled={loading || inputValue.length === 0}
            className="w-full sm:w-auto bg-gv-yellow text-light-black font-semibold text-xs
              px-4 py-3 rounded-lg whitespace-nowrap
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <CircularProgress color="inherit" size={16} />
            ) : (
              "Apply Code"
            )}
          </button>
        </div>

        {errorMsg && (
          <p className="text-sm text-red-500">{errorMsg}</p>
        )}

        {appliedCodes.length > 0 && (
          <div className="flex flex-col gap-3">
            {appliedCodes.map((code) => (
              <PromoCode
                key={code._id}
                promoCode={code}
                handleRemoveCode={handleRemoveCode}
              />
            ))}
            {successMsg && (
              <p className="text-sm text-light-black">
                <strong>{"✓ Promo Applied:"}</strong> {successMsg}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
